import { cn } from "@babybjorn/ui";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useState, type ChangeEventHandler, type FC } from "react";
import { copy } from "../../../../../utils/share";
import Button from "../../../Button";
import Icon, { type IconType } from "../../../Icon/Icon";
import Input from "../../../Input";
import * as s from "./ShareListItem.module.scss";

type SocialItem = {
	icon: IconType | undefined;
	label: string;
	onClick?: () => void;
};

type ShareListItemProps = {
	socials: SocialItem[];
};

const ShareListItem: FC<ShareListItemProps> = ({ socials }) => {
	const { t } = useTranslation();
	const [input, setInput] = useState("");
	const [touched, setTouched] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const validate = (email: string): boolean => {
		if (
			email.length > 0 &&
			!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)
		) {
			setErrorMessage(t("Invalid email"));
			return false;
		}

		setErrorMessage(undefined);
		return true;
	};

	const onInputChange: ChangeEventHandler<HTMLInputElement> = (changeEvent) => {
		const input = changeEvent.currentTarget.value;
		setInput(input);
		if (touched) validate(input);
	};

	const onInputBlur = () => {
		setTouched(true);
		validate(input);
	};

	const reset = () => {
		setTouched(false);
		setInput("");
	};

	const [copyLabel, setCopyLabel] = useState(t("Copy link"));

	const copyLink = async () => {
		await copy();
		setCopyLabel(t("Copied"));
	};

	const hasError = !!(errorMessage && errorMessage.length > 0);

	return (
		<section className={s.wrapper}>
			{
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
				<li className={s.itemContainer} onClick={() => void copyLink()}>
					<Button
						buttonType="icon"
						variant="outlined"
						icon="link"
						onClick={() => void copyLink()}
					/>
					<p>{copyLabel}</p>
				</li>
			}
			{socials.map((social, index) => (
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
				<li className={s.itemContainer} onClick={social.onClick} key={index}>
					<Button
						buttonType="icon"
						variant="outlined"
						icon={social.icon}
						onClick={social.onClick}
					/>
					<p>{social.label}</p>
				</li>
			))}
			<div className={s.emailContainer}>
				<Input
					className={s.emailInput}
					placeholder={t("Enter recipients email here")}
					errorMessage={errorMessage}
					error={hasError}
					onChange={onInputChange}
					onBlur={onInputBlur}
					type="text"
					value={input}
					required={false}
				/>
				<a
					className={cn(s.emailBtn, { [s.disabled]: hasError })}
					href={`mailto:${input}?subject=${t("BabyBjörn Recommendation")}
					&body=${t(
						"Check out this product from BabyBjörn: ",
					)}%20${window.location.href.toString()}%0D%0A`}
					onClick={() => {
						validate(input);
						setTimeout(reset, 500);
					}}
				>
					<Icon icon="arrow-right" color="inherit" />
				</a>
			</div>
		</section>
	);
};
export default ShareListItem;
